/**
 * Soul Particles Effect
 * Small luminous particles gather from the edges toward the center,
 * as if breathing life/soul into the clothing.
 */

class SoulParticles {
  constructor(canvas) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.particles = [];
    this.active = false;
    this.animId = null;
    this.centerGlow = 0;
    this.resizeObserver = null;
    this.init();
  }

  init() {
    this.resize();
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.canvas.parentElement);
  }

  resize() {
    const parent = this.canvas.parentElement;
    const rect = parent.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = rect.width * dpr;
    this.canvas.height = rect.height * dpr;
    this.canvas.style.width = rect.width + 'px';
    this.canvas.style.height = rect.height + 'px';
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.w = rect.width;
    this.h = rect.height;
    this.cx = this.w / 2;
    this.cy = this.h / 2;
  }

  spawnParticle() {
    // Spawn from edges and corners
    const side = Math.random();
    let x, y;

    if (side < 0.25) {
      // Top
      x = Math.random() * this.w;
      y = -10 - Math.random() * 30;
    } else if (side < 0.5) {
      // Bottom
      x = Math.random() * this.w;
      y = this.h + 10 + Math.random() * 30;
    } else if (side < 0.75) {
      // Left
      x = -10 - Math.random() * 30;
      y = Math.random() * this.h;
    } else {
      // Right
      x = this.w + 10 + Math.random() * 30;
      y = Math.random() * this.h;
    }

    // Target: center area with some spread
    const targetX = this.cx + (Math.random() - 0.5) * this.w * 0.15;
    const targetY = this.cy + (Math.random() - 0.5) * this.h * 0.15;

    // Color palette: warm whites, golds, soft blues
    const colors = [
      { r: 255, g: 255, b: 255 },   // pure white
      { r: 255, g: 248, b: 220 },   // warm cream
      { r: 255, g: 223, b: 170 },   // soft gold
      { r: 200, g: 220, b: 255 },   // pale blue
      { r: 255, g: 210, b: 180 },   // warm peach
      { r: 230, g: 240, b: 255 },   // ice white
    ];
    const color = colors[Math.floor(Math.random() * colors.length)];

    return {
      x: x,
      y: y,
      targetX: targetX,
      targetY: targetY,
      size: 0.5 + Math.random() * 1.2,
      speed: 0.008 + Math.random() * 0.025,
      progress: 0,
      alpha: 0,
      fadeIn: 0.02 + Math.random() * 0.03,
      color: color,
      wobbleX: (Math.random() - 0.5) * 2,
      wobbleY: (Math.random() - 0.5) * 2,
      wobbleSpeed: 0.02 + Math.random() * 0.04,
      wobblePhase: Math.random() * Math.PI * 2,
      trail: [],
      arrived: false
    };
  }

  start() {
    this.active = true;
    this.particles = [];
    this.centerGlow = 0;

    // Initial burst of particles
    for (let i = 0; i < 15; i++) {
      const p = this.spawnParticle();
      p.progress = Math.random() * 0.3; // stagger start
      this.particles.push(p);
    }

    if (!this.animId) this.animate();
  }

  stop() {
    this.active = false;
    // Let remaining particles fade out naturally
  }

  easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  animate() {
    const ctx = this.ctx;
    ctx.clearRect(0, 0, this.w, this.h);

    // Spawn new particles while active
    if (this.active && this.particles.length < 60 && Math.random() < 0.4) {
      this.particles.push(this.spawnParticle());
    }

    // Center glow effect
    if (this.active) {
      this.centerGlow = Math.min(this.centerGlow + 0.008, 0.35);
    } else {
      this.centerGlow = Math.max(this.centerGlow - 0.02, 0);
    }

    if (this.centerGlow > 0.01) {
      const glowRadius = Math.min(this.w, this.h) * 0.3;
      const gradient = ctx.createRadialGradient(
        this.cx, this.cy, 0,
        this.cx, this.cy, glowRadius
      );
      gradient.addColorStop(0, `rgba(255, 245, 230, ${this.centerGlow * 0.4})`);
      gradient.addColorStop(0.4, `rgba(255, 240, 210, ${this.centerGlow * 0.15})`);
      gradient.addColorStop(1, 'rgba(255, 240, 210, 0)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, this.w, this.h);
    }

    // Update and draw particles
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];

      // Progress toward target
      p.progress += p.speed;
      const ease = this.easeInOutCubic(Math.min(p.progress, 1));

      // Wobble for organic movement
      p.wobblePhase += p.wobbleSpeed;
      const wobbleX = Math.sin(p.wobblePhase) * p.wobbleX * (1 - ease);
      const wobbleY = Math.cos(p.wobblePhase * 1.3) * p.wobbleY * (1 - ease);

      // Position: lerp from start to target with wobble
      const startX = p.x;
      const startY = p.y;
      const currentX = startX + (p.targetX - startX) * ease + wobbleX * 20;
      const currentY = startY + (p.targetY - startY) * ease + wobbleY * 20;

      // Store trail
      p.trail.push({ x: currentX, y: currentY, alpha: p.alpha * 0.5 });
      if (p.trail.length > 6) p.trail.shift();

      // Alpha: fade in, then fade when arrived
      if (p.progress < 0.15) {
        p.alpha = Math.min(p.alpha + p.fadeIn, 0.9);
      } else if (p.progress > 0.85) {
        p.alpha = Math.max(p.alpha - 0.03, 0);
        if (!p.arrived) {
          p.arrived = true;
          p.size *= 1.5; // brief expand on arrival
        }
        p.size *= 0.97; // shrink
      } else {
        p.alpha = Math.min(p.alpha + p.fadeIn * 0.5, 0.85);
      }

      // Draw trail
      for (let t = 0; t < p.trail.length; t++) {
        const tr = p.trail[t];
        const trAlpha = (t / p.trail.length) * tr.alpha * 0.3;
        if (trAlpha > 0.01) {
          ctx.beginPath();
          ctx.arc(tr.x, tr.y, p.size * 0.5, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${p.color.r}, ${p.color.g}, ${p.color.b}, ${trAlpha})`;
          ctx.fill();
        }
      }

      // Draw main particle
      if (p.alpha > 0.01) {
        // Outer glow
        const glowSize = p.size * 3;
        const glow = ctx.createRadialGradient(
          currentX, currentY, 0,
          currentX, currentY, glowSize
        );
        glow.addColorStop(0, `rgba(${p.color.r}, ${p.color.g}, ${p.color.b}, ${p.alpha * 0.3})`);
        glow.addColorStop(1, `rgba(${p.color.r}, ${p.color.g}, ${p.color.b}, 0)`);
        ctx.fillStyle = glow;
        ctx.fillRect(currentX - glowSize, currentY - glowSize, glowSize * 2, glowSize * 2);

        // Core
        ctx.beginPath();
        ctx.arc(currentX, currentY, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${p.color.r}, ${p.color.g}, ${p.color.b}, ${p.alpha})`;
        ctx.fill();

        // Bright center
        ctx.beginPath();
        ctx.arc(currentX, currentY, p.size * 0.4, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${p.alpha * 0.9})`;
        ctx.fill();
      }

      // Remove dead particles
      if (p.progress > 1.2 || (p.alpha < 0.01 && p.progress > 0.9)) {
        this.particles.splice(i, 1);
        // Respawn if still active
        if (this.active) {
          this.particles.push(this.spawnParticle());
        }
      }
    }

    // Continue animation if particles remain or active
    if (this.particles.length > 0 || this.active || this.centerGlow > 0.01) {
      this.animId = requestAnimationFrame(() => this.animate());
    } else {
      this.animId = null;
      ctx.clearRect(0, 0, this.w, this.h);
    }
  }

  destroy() {
    this.active = false;
    if (this.animId) cancelAnimationFrame(this.animId);
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }
}

// Expose globally
window.SoulParticles = SoulParticles;
